import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { CSVLink } from "react-csv";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import TopHeader from "../../Components/TopHeader";
import SearchBar from "../../Components/common/SearchBar";
import OrganisationLayout from "../layout/OrganisationLayout";
import { getOrganisationRoleAccess } from "../utils/roleAccess";
import {
  truncateEmail,
  truncateTextbrowse,
  truncateTextNames,
} from "../../Util/HelperFunction";

const staffList = [
  {
    _id: "org-staff-1",
    firstName: "Harvey",
    surName: "James",
    department: "Northgate College",
    email: "harvey.james@northgate.edu",
    accountType: "Group Admin",
    image: "",
  },
  {
    _id: "org-staff-2",
    firstName: "Ava",
    surName: "Carter",
    department: "Riverside School",
    email: "ava.carter@riverside.edu",
    accountType: "Group Collaborator",
    image: "",
  },
  {
    _id: "org-staff-3",
    firstName: "Tia",
    surName: "Morgan",
    department: "Elmwood Academy",
    email: "tia.morgan@elmwood.edu",
    accountType: "Teacher",
    image: "",
  },
  {
    _id: "org-staff-4",
    firstName: "Noah",
    surName: "Bennett",
    department: "Westbridge Campus",
    email: "noah.bennett@westbridge.edu",
    accountType: "Group Super Admin",
    image: "",
  },
  {
    _id: "org-staff-5",
    firstName: "Grace",
    surName: "Ellis",
    department: "Northgate College",
    email: "grace.ellis@northgate.edu",
    accountType: "Teacher",
    image: "",
  },
  {
    _id: "org-staff-6",
    firstName: "Leo",
    surName: "Turner",
    department: "Riverside School",
    email: "leo.turner@riverside.edu",
    accountType: "School Admin",
    image: "",
  },
  {
    _id: "org-staff-7",
    firstName: "Mia",
    surName: "Walsh",
    department: "Elmwood Academy",
    email: "mia.walsh@elmwood.edu",
    accountType: "Group Collaborator",
    image: "",
  },
  {
    _id: "org-staff-8",
    firstName: "Owen",
    surName: "Price",
    department: "Westbridge Campus",
    email: "owen.price@westbridge.edu",
    accountType: "Teacher",
    image: "",
  },
];

export default function OrganisationGroupsStaff() {
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const navigate = useNavigate();
  const access = getOrganisationRoleAccess(userData);

  const [searchValue, setSearchValue] = useState("");
  const [department, setDepartment] = useState("");

  const departmentOptions = useMemo(
    () => [...new Set(staffList.map((item) => item.department).filter(Boolean))],
    []
  );

  const filteredStaff = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return staffList.filter((item) => {
      const fullName = `${item.firstName || ""} ${item.surName || ""}`.trim().toLowerCase();
      const matchesSearch =
        !normalizedSearch ||
        fullName.includes(normalizedSearch) ||
        (item.email || "").toLowerCase().includes(normalizedSearch) ||
        (item.department || "").toLowerCase().includes(normalizedSearch) ||
        (item.accountType || "").toLowerCase().includes(normalizedSearch);

      const matchesDepartment = !department || item.department === department;

      return matchesSearch && matchesDepartment;
    });
  }, [searchValue, department]);

  const csvStaffData = useMemo(
    () =>
      filteredStaff.map((record) => ({
        firstname: record.firstName,
        surname: record.surName,
        department: record.department,
        email: record.email,
      })),
    [filteredStaff]
  );

  const handleDetailClick = (data) => {
    navigate(`/organisations/teacher-detail/${data?._id}`);
  };

  const handleDepartmentSelect = (e) => {
    setDepartment(e.target.value);
  };

  const handlePrimaryAction = () => {
    toast.success("Static organisation staff UI only. Actions will be wired later.", {
      iconTheme: {
        primary: "#4318ff",
        secondary: "#FFFAEE",
      },
    });
  };

  const handleUploadClick = () => {
    toast.success("CSV upload will be added once organisation APIs are ready.", {
      iconTheme: {
        primary: "#4318ff",
        secondary: "#FFFAEE",
      },
    });
  };

  return (
    <OrganisationLayout>
      <TopHeader setSearchValue={setSearchValue} groupData={[]} type="Class" />

      <div className="heading-top mt-5 mb-2">
        <h2>All Staff</h2>
      </div>
      <Container fluid>
        <Row>
          <Col lg={4}>
            <SearchBar setSearchValue={setSearchValue} />
          </Col>
          <Col lg={3}>
            <div className="custom-select-wrapper">
              <Form.Select
                aria-label="Default select example"
                className="cmn-select"
                onChange={handleDepartmentSelect}
                value={department}
              >
                <option value="">All Member Organisations</option>
                {departmentOptions.map((item) => (
                  <option value={item} key={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
          <Col lg={5} className="create-new-btn d-flex justify-content-end">
            <button className="cmn-pink" onClick={handlePrimaryAction}>
              Edit Permissions
            </button>

            {access.canEditPermissions && (
              <button className="cmn-sky" onClick={handleUploadClick}>
                Upload CSV
              </button>
            )}

            {access.canEditPermissions && (
              <button className="modal-f-btn btn btn-primary download-csv">
                <CSVLink
                  data={csvStaffData}
                  headers={[
                    { label: "firstname", key: "firstname" },
                    { label: "surname", key: "surname" },
                    { label: "department", key: "department" },
                    { label: "email", key: "email" },
                  ]}
                  filename="organisation-staff.csv"
                  target="_blank"
                  style={{ textDecoration: "none", color: "#fff" }}
                >
                  Download CSV
                </CSVLink>
              </button>
            )}
          </Col>
        </Row>
      </Container>
      <Container fluid>
        {filteredStaff && filteredStaff.length > 0 ? (
          <div className="students-main">
            {filteredStaff.map((ele, index) => (
              <div
                className="students-list"
                onClick={() => handleDetailClick(ele)}
                style={{ cursor: "pointer" }}
                key={index}
              >
                <img
                  src={
                    ele.image ? ele.image : require("../../Assets/Images/girl.png")
                  }
                  alt=""
                />
                <h3>
                  {ele.firstName && truncateTextNames(ele.firstName)}{" "}
                  {ele.surName && truncateTextNames(ele.surName)}
                </h3>
                <p>
                  {ele.department ? truncateTextbrowse(ele.department, 1) : "N/A"}{" "}
                </p>

                {ele?.email && (
                  <p className="all-learners-mail">
                    {truncateEmail(ele.email, 20)}
                  </p>
                )}
                {ele?.accountType && (
                  <p className="all-learners-mail">{ele.accountType}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="data-not-found">
            <p>No Data Found</p>
          </div>
        )}
      </Container>
    </OrganisationLayout>
  );
}
